import { SupportTrip } from "domain/entities/support-trip";
import { MessageTypeEnum } from "domain/enum/messageType";
import { SupportTripStatusEnum } from "domain/enum/supportTripStatus";
import Spinner from "presentation/components/Shared/Spinner";
import { useSupportTripById, useUpdateSupportTripMutation } from "presentation/hooks/use-query-support-trip";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";

interface ParamProps {
    supportTrip: SupportTrip | undefined;
    onClose(): void;
    showMessage(
        type: MessageTypeEnum,
        subject: string,
        message: string
    ): void;
}

const ApprovalForm: React.FC<ParamProps> = (params: ParamProps) => {

    const toast = useRef<Toast>(null);
    const { userEmployee, email: userEmployeeEmail } = useAuthStore();
    const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
    const [reasonReject, setReasonReject] = useState("");
    const possibleApprovalStatus = ["A Aprovar"];

    const { mutate: updateSupportTrip, isSuccess: isSuccessUpdateSupportTrip, isError: isErrorUpdateSupportTrip, isPending: isPendingUpdateSupportTrip } =
        useUpdateSupportTripMutation();

    const { data: supportTrip } = useSupportTripById(
        params.supportTrip?.id
    );

    useEffect(() => {
        if (isSuccessUpdateSupportTrip) {
            if (supportTrip?.data?.status === SupportTripStatusEnum.APROVADO) {
                params.showMessage(
                    MessageTypeEnum.SUCCESS,
                    "Workflow",
                    "Viagem foi aprovada com sucesso"
                );
            } else if (supportTrip?.data?.status === SupportTripStatusEnum.NAO_APROVADO) {
                params.showMessage(
                    MessageTypeEnum.SUCCESS,
                    "Workflow",
                    "Viagem foi reprovada com sucesso"
                );
            }

            params.onClose();
        }
    }, [isSuccessUpdateSupportTrip]);


    useEffect(() => {
        if (isErrorUpdateSupportTrip) {
            if (supportTrip?.data?.status === SupportTripStatusEnum.APROVADO) {
                params.showMessage(
                  MessageTypeEnum.ERROR,
                  "Workflow",
                  "Erro ao aprovar viagem"
                );
            } else if (supportTrip?.data?.status === SupportTripStatusEnum.NAO_APROVADO) {
                params.showMessage(
                  MessageTypeEnum.ERROR,
                  "Workflow",
                  "Erro ao reprovar viagem"
                );
            }

            params.onClose();
        }
    }, [isErrorUpdateSupportTrip]);

    const handleApprove = () => {
        const supportTripData = supportTrip?.data;
        if (supportTripData && supportTripData.status === SupportTripStatusEnum.A_APROVAR && userEmployeeEmail) {
            supportTripData.status = SupportTripStatusEnum.APROVADO;
            supportTripData.lastModifiedBy = userEmployeeEmail;
            updateSupportTrip(supportTripData);
        }
        setShowConfirm(false);
        setConfirmAction(null);
        params.onClose();
    };

    const handleReject = () => {
        const supportTripData = supportTrip?.data;
        if (supportTripData && supportTripData.status === SupportTripStatusEnum.A_APROVAR && userEmployeeEmail) {
            supportTripData.status = SupportTripStatusEnum.NAO_APROVADO;
            supportTripData.rejectionReason = reasonReject;
            supportTripData.lastModifiedBy = userEmployeeEmail;
            updateSupportTrip(supportTripData);
        }
        setVisibleRejectionReason(false);
        setConfirmAction(null);
        params.onClose();
    };

    if (isPendingUpdateSupportTrip) return <Spinner />;

    return (
        <div>
            <div>
                <ConfirmDialog
                    visible={showConfirm}
                    onHide={() => setShowConfirm(false)}
                    message={
                        confirmAction === 'approve'
                            ? 'Deseja aprovar esta viagem?'
                            : 'Deseja rejeitar esta viagem, sendo este o motivo?'}
                    header="Confirmação"
                    icon="pi pi-exclamation-triangle"
                    accept={() => {
                        if (confirmAction === "approve") handleApprove();
                        if (confirmAction === "reject") {
                            setVisibleRejectionReason(false);
                            handleReject();
                        }
                    }}
                    reject={() => setShowConfirm(false)}
                    acceptLabel="Sim"
                    rejectLabel="Não"
                />

                {
                    <div className="vertical-list">

                        <div><strong>Colaborador:</strong> {supportTrip?.data?.employee.name}</div>
                        <div><strong>Data:</strong> {formatDateToPT(supportTrip?.data?.dateTrip)}</div>
                        <div><strong>Origem:</strong> {supportTrip?.data?.locationOrigin}</div>
                        <div><strong>Destino:</strong> {supportTrip?.data?.locationDestination}</div>
                        <div><strong>Distância:</strong> {supportTrip?.data?.distanceKm} km</div>
                        <div><strong>Valor:</strong> {supportTrip?.data?.tripValue.toFixed(2)} €</div>
                        <div><strong>Estado:</strong> {supportTrip?.data?.status}</div>



                    </div>

                }

                {possibleApprovalStatus.includes(params.supportTrip?.status ?? "") && (
                    <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "auto", padding: "50px 0" }}>
                        <Button
                            label="Rejeitar"
                            className="p-button-danger"
                            icon="pi pi-times"
                            onClick={() => {
                                setReasonReject("");
                                setVisibleRejectionReason(true);
                            }}
                        />
                        <Button
                            label="Aprovar"
                            icon="pi pi-check"
                            onClick={() => {
                                setConfirmAction("approve");
                                setShowConfirm(true);
                            }}
                        />
                    </div>
                )
                }

            </div>
            <Dialog
                header="Rejeição da Viagem"
                visible={visibleRejectionReason}
                style={{ width: "30vw" }}
                modal
                onHide={() => setVisibleRejectionReason(false)}
            >
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="motivo">Informe o Motivo da Rejeição:</label>
                        <InputTextarea
                            id="motivo"
                            value={reasonReject}
                            onChange={(e) => setReasonReject(e.target.value)}
                            rows={4}
                            autoFocus
                            maxLength={190}
                        />
                        <div className="flex justify-content-end">
                            <small>{reasonReject?.length}/190 caracteres</small>
                        </div>
                    </div>
                    <div className="flex justify-content-end gap-2 mt-3">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            onClick={() => setVisibleRejectionReason(false)}
                        />
                        <Button
                            label="Rejeitar"
                            icon="pi pi-check"
                            onClick={() => {
                                if (reasonReject.trim() !== "") {
                                    setConfirmAction("reject");
                                    setShowConfirm(true);
                                } else {
                                    toast.current?.show({
                                        severity: "error",
                                        summary: "Erro",
                                        detail: "Informe o motivo da não aprovação da viagem.",
                                        life: 6000,
                                    });
                                }
                            }}
                            autoFocus
                        />
                    </div>
                </div>
            </Dialog>

            <Toast ref={toast} position="bottom-right" />

        </div>
    );
};

export default ApprovalForm;
